import { cn } from "@/lib/utils";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Textarea } from "@/shared/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { useGetSingleImage, useSaveImageEdit, useUndoEditImage } from "./api";
import { useNavigate, useParams } from "react-router-dom";
import { Stage, Layer, Image as KonvaImage } from "react-konva";
import { AlignLeft, AlignCenter, AlignRight, Trash2, ArrowUp, Loader2, Type, ImagePlus, Download, Undo, ArrowBigLeftDash, Square, Crop, SquareDashed, RectangleVertical } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Slider } from "@/shared/ui/slider";
import { ImageAttr, TextLayerAttr } from "./types";
import ImageLayer from "@/shared/layer-editor/ImageLayer";
import TextLayer from "@/shared/layer-editor/TextLayer";
import RectLayer, { RectLayerAttr } from "@/shared/layer-editor/ShapeLayer";
import Konva from "konva";
import InputColor from "@/shared/inputs/ColorInput";
import BaseImageCrop from "@/shared/layer-editor/BaseImageCrop";
import ToolTipTitle from "@/shared/ui/tooltip-title";

type LayerType = 'image' | 'text' | 'shape';

type EditorLayer =
  | ({ type: 'image' } & ImageAttr)
  | ({ type: 'text' } & TextLayerAttr)
  | ({ type: 'shape' } & RectLayerAttr);

const TextLayerEditor = () => {

  const { saveImageEdit, status } = useSaveImageEdit()

  const [isImageLoading, setIsImageLoading] = useState(true)

  const { imageId } = useParams()

  const { imageData } = useGetSingleImage(imageId)

  const [img, setImg] = useState<HTMLImageElement | null>(null);

  const [showCrop, setShowCrop] = useState(false)

  const [isVideo, setIsVideo] = useState(false)

  const mainImageRef = useRef<Konva.Image>(null);

  // Filters for the main image
  const [filters, setFilters] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
  });


  const [layers, setLayers] = useState<EditorLayer[]>([]);

  const [cropAspect, setCropAspect] = useState<number>(1)

  // Deselect when clicking on empty area
  const checkDeselect = (e: any) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      setSelectionType(null);
    }
  };

  const addShapeLayer = () => {
    const newLayer: EditorLayer = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      color: "#ffffff",
      width: 50,
      height: 50,
      corderRadius: 0,
      opacity: 1,
      x: 50,
      y: 50,
    };
    setLayers(prev => [...prev, newLayer]);
    setSelectedId(newLayer.id);
    setSelectionType('shape');
  };

  const addTextLayer = () => {
    const newLayer: EditorLayer = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: "New Text",
      font: "Poppins",
      fontSize: 30,
      color: "#ffffff",
      textSpacing: 1,
      fontWeight: "normal",
      textAlign: "left",
      lineHeight: 1.2,
      textWidth: 300,
      x: 50,
      y: 50,
    };
    setLayers(prev => [...prev, newLayer]);
    setSelectedId(newLayer.id);
    setSelectionType('text');
  };

  // Delete selected text layer

  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 500,
    height: 500,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectionType, setSelectionType] = useState<'text' | 'image' | 'shape' | null>('text');

  const selectedLayer = layers.find(l => l.id === selectedId);
  const selectedTextLayer = selectedLayer?.type === 'text' ? selectedLayer : null;
  const selectedShapeLayer = selectedLayer?.type === 'shape' ? selectedLayer : null;

  const updateTextLayer = (updates: Partial<TextLayerAttr>) => {
    if (!selectedId) return;
    setLayers(prev =>
      prev.map(l => (l.id === selectedId && l.type === 'text' ? { ...l, ...updates } : l))
    );
  };

  const updateShapeLayer = (updates: Partial<RectLayerAttr>) => {
    if (!selectedId) return;
    setLayers(prev =>
      prev.map(l => (l.id === selectedId && l.type === 'shape' ? { ...l, ...updates } : l))
    );
  };
  const stageRef = useRef<any>(null);

  const updateFilter = (key: keyof typeof filters, value: number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Load demo image
  useEffect(() => {
    if (imageData) {
      if (imageData.type !== "video") {
        const image = new window.Image();
        image.src = imageData.image_url; // replace with uploaded image later
        image.crossOrigin = "anonymous";
        image.onload = () => {
          setImg(image);
          if (image.width > 2000 || image.height > 2000) {
            setDimensions({ width: 512, height: 512 });
          } else {
            setDimensions({ width: image.width / 2, height: image.height / 2 });
          }
          setIsImageLoading(false)
        };
      } else {
        setIsVideo(true)
      }
    }
  }, [imageData]);

  useEffect(() => {
    const node = mainImageRef.current;
    if (node) {
      node.cache();
      node.filters([
        Konva.Filters.Brighten,
        Konva.Filters.Contrast,
        Konva.Filters.HSL,
      ]);
      node.brightness(filters.brightness);
      node.contrast(filters.contrast);
      node.saturation(filters.saturation);
      node.getLayer()?.batchDraw();
    }
  }, [filters]);

  const downloadImage = () => {

    if (stageRef.current && imageId) {
      // Deselect all before saving
      setSelectedId(null);
      setSelectionType(null);

      // Wait for transformers to hide
      setTimeout(() => {
        const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
        const link = document.createElement("a");
        link.download = "edited-image.png";
        link.href = uri;
        link.click();
      }, 100);
    }

  };

  const getLayersByType = (type: LayerType) => layers.filter(layer => layer.type === type);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const image = new window.Image();
      image.src = event.target?.result as string;
      image.onload = () => {
        const maxSize = 200;
        const scale = Math.min(maxSize / image.width, maxSize / image.height);

        const newImage: EditorLayer = {
          id: `img-${Date.now()}`,
          type: 'image',
          image,
          x: 50,
          y: 50,
          width: image.width * scale,
          height: image.height * scale,
          originalWidth: image.width,
          originalHeight: image.height,
        };

        setLayers(prev => [...prev, newImage]);
        setSelectedId(newImage.id);
        setSelectionType('image');
      };
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Delete selected uploaded image
  const handleDeleteLayer = () => {
    if (!selectedId) return;
    setLayers(prev => prev.filter(layer => layer.id !== selectedId));
    setSelectedId(null);
    setSelectionType(null);
  };

  const handleBringToTop = () => {
    if (!selectedId) return;
    setLayers(prev => {
      const selected = prev.find(l => l.id === selectedId);
      if (!selected) return prev;
      const others = prev.filter(l => l.id !== selectedId);
      return [...others, selected];
    });
  };

  const saveImageHandler = () => {
    if (stageRef.current && imageId) {
      // Deselect all before saving
      setSelectedId(null);
      setSelectionType(null);

      // Wait for transformers to hide
      setTimeout(() => {
        const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
        console.log(uri);
        saveImageEdit({
          image_id: parseInt(imageId),
          image_file: uri
        });
      }, 100);
    }
  };

  const { undoImage, undoStatus, lastSavedImage } = useUndoEditImage(() => { })

  const undoImageHandler = () => {
    if (imageId) {
      undoImage(parseInt(imageId))
    }
  }

  useEffect(() => {
    if (lastSavedImage && status === "success") {

      const image = new window.Image();
      image.src = lastSavedImage.reverted_url; // replace with uploaded image later
      image.crossOrigin = "anonymous";
      image.onload = () => {
        setImg(image);
        console.log(image.width, image.height)
        setDimensions({ width: image.width / 2, height: image.height / 2 });
      };
    }
  }, [lastSavedImage, status])

  const navigate = useNavigate()

  const setBaseImageCrop = (url: string) => {

    const image = new window.Image();
    image.src = url; // replace with uploaded image later
    image.crossOrigin = "anonymous";
    image.onload = () => {
      setImg(image);

      const { width, height } = image;

      // Maximum allowed dimension
      const maxSize = 1024;

      let newWidth = width;
      let newHeight = height;

      // Only resize if it exceeds the max dimension
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          // Landscape orientation
          newWidth = maxSize;
          newHeight = Math.round((height / width) * maxSize);
        } else {
          // Portrait or square orientation
          newHeight = maxSize;
          newWidth = Math.round((width / height) * maxSize);
        }
      }

      setDimensions({ width: newWidth, height: newHeight });
    };

    setShowCrop(false)

  }

  return (
    <div className='w-full flex flex-col lg:flex-row bg-white bg-clip-padding backdrop-filter overflow-hidden backdrop-blur-xl bg-opacity-80 backdrop-saturate-100 shadow-[-1px_4px_12px_-3px_rgba(236,_72,_153,_0.5)] backdrop-contrast-150 sm:w-[calc(100%-2rem)] rounded-3xl h-full mx-auto space-y-3'>
      {isVideo && (
        <section className={cn("w-full h-[1200px] lg:h-full p-4 relative overflow-hidden flex flex-col items-center justify-center")} id="editor-text">
          <div
            className={cn(
              "absolute inset-0",
              "[background-size:30px_30px]",
              "[background-image:radial-gradient(#5512ff_1.2px,transparent_1px)]",
              "dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]",
            )}
          />
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="absolute top-1 left-1 flex items-center gap-2 backdrop-blur-md hover:bg-transparent rounded-full"
          >
            <ArrowBigLeftDash />
            Back
          </Button>
          <h1 className="text-4xl sm:text-6xl lg:text-8xl font-montserrat text-indigo-800">siAi Video Editor</h1>
          <p className="text-xl font-poppins text-gray-400 py-4 bg-white relative">Coming soon...</p>
        </section>
      )}
      <section className={cn("w-full lg:w-3/4 h-[1200px] lg:h-full p-4 relative overflow-hidden",
        isVideo && "hidden"
      )} id="editor-text">
        <div
          className={cn(
            "absolute inset-0",
            "[background-size:30px_30px]",
            "[background-image:radial-gradient(#5512ff_1.2px,transparent_1px)]",
            "dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]",
          )}
        />

        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="absolute top-1 left-1 flex items-center gap-2 backdrop-blur-md hover:bg-transparent rounded-full"
        >
          <ArrowBigLeftDash />
          Back
        </Button>
        {/* Toolbar */}
        {showCrop && (
          <div className={cn("absolute top-[50%] -translate-y-[50%] backdrop-blur flex flex-col gap-3 py-5 px-2 border border-purple-400 rounded-full",
            isVideo && "hidden"
          )}>
            <Button
              className="flex flex-col gap-0 items-center justify-center h-12 w-8"
              onClick={() => setCropAspect(1)}
              variant="ghost">
              <Square />
              <p className="text-[0.6rem] font-poppins">1:1</p>
            </Button>
            <Button
              className="flex flex-col gap-0 items-center justify-center h-12 w-8"
              onClick={() => setCropAspect(9 / 16)}
              variant="ghost">
              <RectangleVertical className="scale-x-75" />
              <p className="text-[0.6rem] font-poppins">9:16</p>
            </Button>
            <Button
              className="flex flex-col gap-0 items-center justify-center h-12 w-8"
              onClick={() => setCropAspect(2 / 3)}
              variant="ghost">
              <RectangleVertical />
              <p className="text-[0.6rem] font-poppins">2:3</p>
            </Button>
            <Button
              className="flex flex-col gap-0 items-center justify-center h-12 w-8"
              onClick={() => setCropAspect(4 / 5)}
              variant="ghost">
              <RectangleVertical className="scale-x-125" />
              <p className="text-[0.6rem] font-poppins">4:5</p>
            </Button>
          </div>
        )}
        {!showCrop &&
          <div className={cn("absolute top-[50%] -translate-y-[50%] backdrop-blur flex flex-col gap-2 py-3 px-2 border border-purple-400 rounded-full",
            isVideo && "hidden"
          )}>
            <ToolTipTitle description="Crop base image" text={
              <Button
                className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                onClick={() => setShowCrop(true)}
                variant="ghost">
                <Crop />
                <p className="text-[0.6rem] font-poppins">Crop</p>
              </Button>
            } />
            <ToolTipTitle
              description="Add Shape"
              text={
                <Button
                  className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                  onClick={addShapeLayer}
                  variant="ghost"
                >
                  <SquareDashed />
                  <p className="text-[0.6rem] font-poppins">Shape</p>
                </Button>
              }
            />
            <ToolTipTitle text={
              <Button
                className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                onClick={addTextLayer}
                variant="ghost"
              >
                <Type />
                <p className="text-[0.6rem] font-poppins">Text</p>
              </Button>
            } description="Add Text" />
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <ToolTipTitle description="Add image" text={
              <Button
                className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                onClick={() => fileInputRef.current?.click()}
                variant="ghost">
                <ImagePlus />
                <p className="text-[0.6rem] font-poppins">Image</p>
              </Button>
            } />
            <ToolTipTitle description="Bring to top" text={
              <Button
                className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                type="button"
                onClick={handleBringToTop}
                variant="ghost"
              >
                <ArrowUp />
                <p className="text-[0.6rem] font-poppins">Move up</p>
              </Button>
            } />
            {selectedId &&
              <ToolTipTitle description="Delete selection" text={
                <Button
                  className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                  type="button"
                  onClick={handleDeleteLayer}
                  variant="ghost"
                >
                  <Trash2 className="text-red-500" />
                  <p className="text-[0.6rem] font-poppins">Delete</p>
                </Button>
              } />
            }


            <ToolTipTitle
              description="Download Image"
              text={
                <Button
                  className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                  variant="ghost" onClick={downloadImage}>
                  <Download />
                  <p className="text-[0.6rem] font-poppins">Save</p>
                </Button>
              }
            />

            {undoStatus === "pending"
              ? <ToolTipTitle
                description="Undoing changes"
                text={
                  <Button
                    variant="ghost"
                  >
                    <Loader2 className="animate-spin" />
                  </Button>
                }
              />
              : <ToolTipTitle
                description="Undo Last Change"
                text={
                  <Button
                    className="flex flex-col gap-0 items-center justify-center h-12 w-9"
                    onClick={undoImageHandler}
                    variant="ghost"
                  >
                    <Undo />
                    <p className="text-[0.6rem] font-poppins">Undo</p>
                  </Button>
                }
              />

            }

          </div>}
        {/* Canvas */}
        <div className="w-fit mx-auto pt-16">
          {showCrop && imageData &&
            <BaseImageCrop
              baseImage={imageData.image_url}
              aspect={cropAspect}
              onCrop={setBaseImageCrop}
              onCropCancel={() => setShowCrop(false)}
            />
          }
          {isImageLoading &&
            <div className="w-1/2 aspect-square bg-black/20 rounded-xl text-white backdrop-blur-[5px] z-50 absolute top-[15%] left-[50%] -translate-x-[50%] flex flex-col gap-4 items-center justify-center">
              <Loader2 className="animate-spin" />
              <p className="font-poppins">Loading Image</p>
            </div>
          }
          {!showCrop &&
            <Stage
              width={dimensions.width}
              height={dimensions.height}
              ref={stageRef}
              onMouseDown={checkDeselect}
              onTouchStart={checkDeselect}
              className="border"
            >
              <Layer>
                {img && (
                  <KonvaImage
                    ref={mainImageRef}
                    image={img}
                    width={dimensions.width}
                    height={dimensions.height}
                    onClick={() => {
                      setSelectedId(null);
                      setSelectionType(null);
                    }}
                  />
                )}

                {/* Uploaded images */}
                {layers.map((layer, i) => {
                  if (layer.type === 'image') {
                    return (
                      <ImageLayer
                        key={layer.id}
                        imageAttr={layer}
                        isSelected={layer.id === selectedId}
                        onSelect={() => {
                          setSelectedId(layer.id);
                          setSelectionType('image');
                        }}
                        onChange={(newAttrs) => {
                          const updated = { ...layer, ...newAttrs };
                          const newLayers = layers.slice();
                          newLayers[i] = updated;
                          setLayers(newLayers);
                        }}
                      />
                    );
                  }

                  if (layer.type === 'text') {
                    return (
                      <TextLayer
                        key={layer.id}
                        textAttr={layer}
                        isSelected={layer.id === selectedId}
                        onSelect={() => {
                          setSelectedId(layer.id);
                          setSelectionType('text');
                        }}
                        onChange={(newAttrs) => {
                          const updated = { ...layer, ...newAttrs };
                          const newLayers = layers.slice();
                          newLayers[i] = updated;
                          setLayers(newLayers);
                        }}
                      />
                    );
                  }

                  if (layer.type === 'shape') {
                    return (
                      <RectLayer
                        key={layer.id}
                        shapeAttr={layer}
                        isSelected={layer.id === selectedId}
                        onSelect={() => {
                          setSelectedId(layer.id);
                          setSelectionType('shape');
                        }}
                        onChange={(newAttrs) => {
                          const updated = { ...layer, ...newAttrs };
                          const newLayers = layers.slice();
                          newLayers[i] = updated;
                          setLayers(newLayers);
                        }}
                      />
                    );
                  }

                  return null;
                })}

              </Layer>
            </Stage>}

        </div>
      </section>
      <section className={cn("w-full lg:w-1/4 bg-gradient-to-b from-white to-purple-50 !mt-0 border-s-indigo-300 border-s h-full p-2 flex flex-col gap-4 px-3 overflow-y-auto",
        isVideo && "hidden"
      )} id="editor-siai">

        <div className="flex items-center justify-between">
          <p className="font-montserrat tracking-wide font-semibold text-purple-500">siAi Image Canvas</p>
        </div>

        {/* Text layer selector */}
        {getLayersByType('text').length > 1 && (
          <div className="flex gap-2 flex-wrap pb-2 border-b border-purple-200">
            {getLayersByType('text').map((layer, index) => (
              <Button
                key={layer.id}
                type="button"
                size="sm"
                variant={selectedId === layer.id && selectionType === 'text' ? "default" : "outline"}
                onClick={() => {
                  setSelectedId(layer.id);
                  setSelectionType('text');
                }}
                className={cn(
                  "h-7 text-xs",
                  selectedId === layer.id && selectionType === 'text' && "bg-gradient-to-r from-purple-500 to-indigo-500"
                )}
              >
                Text {index + 1}
              </Button>
            ))}
          </div>
        )}

        {!selectedTextLayer && !selectedShapeLayer && (
          <>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Brightness</Label>
              <Slider
                value={[filters.brightness]}
                onValueChange={([val]) => updateFilter("brightness", val)}
                min={-1}
                max={1}
                step={0.005}
                className="w-full"
              />
            </div>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Contrast</Label>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={[filters.contrast]}
                onValueChange={([val]) => updateFilter("contrast", val)}
                className="w-full"
              />
            </div>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Saturation</Label>
              <Slider
                min={-2}
                max={2}
                step={0.1}
                value={[filters.saturation]}
                onValueChange={([val]) => updateFilter("saturation", val)}
                className="w-full"
              />
            </div>
          </>
        )}

        {selectedShapeLayer && (
          <>
            <div className="w-full flex items-center gap-1 px-2">
              <InputColor
                label=""
                value={selectedShapeLayer.color}
                onChange={(val: string) => updateShapeLayer({ color: val })}
                onBlur={() => console.log("color picker")}
              />
            </div>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Border Radius</Label>
              <Slider
                min={0}
                max={50}
                step={1}
                value={[selectedShapeLayer.corderRadius]}
                onValueChange={([val]) => updateShapeLayer({ corderRadius: val })}
                className="w-full"
              />
            </div>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Opacity</Label>
              <Slider
                min={0}
                max={1}
                step={0.1}
                value={[selectedShapeLayer.opacity]}
                onValueChange={([val]) => updateShapeLayer({ opacity: val })}
                className="w-full"
              />
            </div>

          </>
        )}

        {selectedTextLayer && (
          <>
            <Label className="text-sm font-poppins font-normal">Input Text</Label>
            <Textarea
              value={selectedTextLayer.text}
              onChange={e => updateTextLayer({ text: e.target.value })}
              className="resize-none"
            />

            <div className="flex gap-2">
              <Select
                value={selectedTextLayer.font}
                onValueChange={(val: string) => updateTextLayer({ font: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a font" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Fonts</SelectLabel>
                    <SelectItem value="Poppins" className="text-xs">Poppins</SelectItem>
                    <SelectItem value="Roboto" className="text-xs">Roboto</SelectItem>
                    <SelectItem value="Montserrat" className="text-xs">Montserrat</SelectItem>
                    <SelectItem value="Arial" className="text-xs">Arial</SelectItem>
                    <SelectItem value="Courier" className="text-xs">Courier</SelectItem>
                    <SelectItem value="Times New Roman" className="text-xs">Times New Roman</SelectItem>
                    <SelectItem value="Noto Serif" className="text-xs">Noto Serif</SelectItem>
                    <SelectItem value="Noto Sans" className="text-xs">Noto Sans</SelectItem>
                    <SelectItem value="Lato" className="text-xs">Lato</SelectItem>
                    <SelectItem value="Playfair Display" className="text-xs">Playfair Display</SelectItem>
                    <SelectItem value="New Spirit" className="text-xs">New Spirit</SelectItem>
                    <SelectItem value="Josefin sans" className="text-xs">Josefin sans</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={selectedTextLayer.fontSize}
                onChange={(e) => updateTextLayer({ fontSize: Number(e.target.value) })}
                className="w-1/2"
              />
            </div>

            <div className="flex lg:flex-col xl:flex-row  gap-2">
              <div className="flex gap-2 px-2">
                <Button
                  type="button"
                  size="icon"
                  variant={selectedTextLayer.textAlign === "left" ? "default" : "outline"}
                  onClick={() => updateTextLayer({ textAlign: "left" })}
                  className={cn("w-7 h-7", selectedTextLayer.textAlign === "left" && "bg-gradient-to-r from-purple-500 to-indigo-500")}
                >
                  <AlignLeft className="w-5 h-5" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant={selectedTextLayer.textAlign === "center" ? "default" : "outline"}
                  onClick={() => updateTextLayer({ textAlign: "center" })}
                  className={cn("w-7 h-7", selectedTextLayer.textAlign === "center" && "bg-gradient-to-r from-purple-500 to-indigo-500 text-white")}
                >
                  <AlignCenter className="w-5 h-5" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant={selectedTextLayer.textAlign === "right" ? "default" : "outline"}
                  onClick={() => updateTextLayer({ textAlign: "right" })}
                  className={cn("w-7 h-7", selectedTextLayer.textAlign === "right" && "bg-gradient-to-r from-purple-500 to-indigo-500 text-white")}
                >
                  <AlignRight className="w-5 h-5" />
                </Button>
              </div>
              <Select
                value={selectedTextLayer.fontWeight}
                onValueChange={(val: string) => updateTextLayer({ fontWeight: val as "normal" | "medium" | "600" | "bold" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select font weight" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Weight</SelectLabel>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="600">Semibold</SelectItem>
                    <SelectItem value="bold">Bold</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

            </div>

            <div className="w-full flex items-center gap-1 px-2">
              <InputColor
                label=""
                value={selectedTextLayer.color}
                onChange={(val: string) => updateTextLayer({ color: val })}
                onBlur={() => console.log("color picker")}
              />
            </div>
            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Letter Spacing</Label>
              <Slider
                value={[selectedTextLayer.textSpacing]}
                onValueChange={(val) => updateTextLayer({ textSpacing: val[0] })}
                min={0.5}
                max={3}
                step={0.1}
                className="w-full"
              />
              <span className="text-xs text-gray-500 text-right">{selectedTextLayer.textSpacing.toFixed(1)}</span>
            </div>

            <div className="w-full flex flex-col gap-2 px-2">
              <Label className="text-sm font-poppins font-normal">Line Height</Label>
              <Slider
                value={[selectedTextLayer.lineHeight]}
                onValueChange={(val) => updateTextLayer({ lineHeight: val[0] })}
                min={0.5}
                max={3}
                step={0.1}
                className="w-full"
              />
              <span className="text-xs text-gray-500 text-right">{selectedTextLayer.lineHeight.toFixed(1)}</span>
            </div>


          </>
        )}


        <Button className="rounded-full border border-purple-500"
          variant="outline"
          onClick={saveImageHandler}
          disabled={status === "pending"}>
          {status === "pending" ? <Loader2 /> : "Save Changes"}
        </Button>
      </section>
    </div>

  )
}

export default TextLayerEditor;
